import { useCallback, useEffect, useMemo, useState } from "react"
import { useSearchParams } from "react-router-dom"
import toast from "react-hot-toast"

import SelectDescription from "components/forms/select-description"
import useBranchRepositories from "repositories/branch"

const toInputDate = (value) => {
  const date = value ? new Date(value) : new Date()
  if (Number.isNaN(date.getTime())) return ""

  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, "0")
  const day = String(date.getDate()).padStart(2, "0")

  return `${year}-${month}-${day}`
}

export default function List() {
  const [searchParams, setSearchParams] = useSearchParams()

  const date = searchParams.get("date") || toInputDate(new Date(Date.now() - 86400000))
  const outletId = searchParams.get("outlet_id") || "ALL"
  const cashier = searchParams.get("cashier") || ""
  const [cashierInput, setCashierInput] = useState(cashier)
  const [isCashierDebouncing, setIsCashierDebouncing] = useState(false)

  const { data: branchSelection, isLoading: isLoadingBranches } = useBranchRepositories({ paginate: false })

  const branchOptions = useMemo(() => {
    const baseOptions = Array.isArray(branchSelection) ? branchSelection : []

    return [
      {
        id: "ALL",
        name: "Semua Outlet",
        description: "Pilih outlet tertentu untuk generate per cabang",
      },
      ...baseOptions,
    ]
  }, [branchSelection])

  const mergeSearchParams = useCallback((nextValues) => {
    const nextParams = new URLSearchParams(searchParams)

    Object.entries(nextValues).forEach(([key, value]) => {
      if (value === null || value === undefined || value === "") {
        nextParams.delete(key)
      } else {
        nextParams.set(key, value)
      }
    })

    setSearchParams(nextParams)
  }, [searchParams, setSearchParams])

  useEffect(() => {
    setCashierInput(cashier)
  }, [cashier])

  useEffect(() => {
    const nextCashier = cashierInput.trim()

    if (nextCashier === cashier) {
      setIsCashierDebouncing(false)
      return undefined
    }

    setIsCashierDebouncing(true)

    const timeoutId = setTimeout(() => {
      mergeSearchParams({ cashier: nextCashier || null })
      setIsCashierDebouncing(false)
    }, 600)

    return () => clearTimeout(timeoutId)
  }, [cashierInput, cashier, mergeSearchParams])

  const handleGenerate = () => {
    if (!date) {
      toast.error("Tanggal wajib diisi sebelum generate")
      return
    }

    if (!outletId || outletId === "ALL") {
      toast.error("Outlet wajib dipilih untuk generate end of day")
      return
    }

    const query = new URLSearchParams({
      date,
      outlet_id: outletId,
    })

    if (cashierInput.trim() !== "") {
      query.set("cashier", cashierInput.trim())
    }

    window.open(
      `${process.env.REACT_APP_INVENTORY_API_URL}/orders/end-of-day-report/export?${query.toString()}`,
      "_blank"
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 p-5 bg-white border border-neutral-200 rounded-2xl lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-lg font-semibold text-neutral-900">End Of Day Report</h1>
          <p className="mt-1 text-sm text-neutral-500">
            Generate laporan harian kasir dalam format Excel berdasarkan `paid_date`.
          </p>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
          <div className="min-w-[220px]">
            <label className="block mb-2 text-xs font-semibold tracking-wide text-neutral-500">
              Outlet
            </label>
            <SelectDescription
              selection={branchOptions}
              isLoading={isLoadingBranches}
              value={outletId}
              onChange={(value) => mergeSearchParams({ outlet_id: value || "ALL" })}
              keyValue={(v) => v.id}
              title={(v) => v.name}
              description={(v) => v.code || v.id}
              placeholder="Select Outlet"
            />
          </div>

          <div className="min-w-[180px]">
            <label className="block mb-2 text-xs font-semibold tracking-wide text-neutral-500">
              Tanggal
            </label>
            <input
              type="date"
              className="w-full px-3 py-2 border rounded-lg border-neutral-300 focus:outline-none focus:ring-2 focus:ring-neutral-800"
              value={date}
              onChange={(event) => mergeSearchParams({ date: event.target.value })}
            />
          </div>

          <div className="min-w-[220px]">
            <label className="block mb-2 text-xs font-semibold tracking-wide text-neutral-500">
              Cashier
            </label>
            <input
              type="text"
              placeholder="Opsional"
              className="w-full px-3 py-2 border rounded-lg border-neutral-300 focus:outline-none focus:ring-2 focus:ring-neutral-800"
              value={cashierInput}
              onChange={(event) => setCashierInput(event.target.value)}
            />
            {isCashierDebouncing && (
              <p className="mt-1 text-xs text-neutral-500">
                Menerapkan filter cashier...
              </p>
            )}
          </div>

          <button
            type="button"
            onClick={handleGenerate}
            className="px-4 py-2 text-sm font-semibold text-white rounded-lg bg-neutral-900 hover:bg-neutral-800"
          >
            Generate Excel
          </button>
        </div>
      </div>

      <div className="p-5 bg-white border border-neutral-200 rounded-2xl">
        <div className="text-sm font-semibold text-neutral-900">Keterangan</div>
        <div className="mt-3 space-y-2 text-sm text-neutral-600">
          <p>Report diambil dari transaksi yang memiliki `paid_date` pada tanggal yang dipilih.</p>
          <p>Filter cashier bersifat opsional.</p>
          <p>Output akan langsung membuka file Excel dari API `Atalla CMS`.</p>
        </div>
      </div>
    </div>
  )
}
